import React, { useState, useEffect, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import DocumentModalComponent from './DocumentModalComponent';
import axios from 'axios';
import Spinner from '../common/Spinner';
import Swal from 'sweetalert2';
import { toast } from 'react-hot-toast';
import { Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel } from "@mui/material";
import Error403 from "../error_pages/Error403"
import Error500 from '../error_pages/Error500';
import fileDownload from 'js-file-download';
import { customAxios } from '../../service/CreateApi';
import usePagination from '../../hooks/usePagination';

const DocumentComponent = () => {
    const [permission, setpermission] = useState("");
    const [documentData, setDocumentData] = useState([]);
    const [searchItem, setsearchItem] = useState("");
    const [isLoading, setisLoading] = useState(false);
    const [toggle, setToggle] = useState(false);
    const [serverError, setServerError] = useState(false);
    const [permissionToggle, setPermissionToggle] = useState(true);

    // pagination state
    const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination(5);

    // sort state
    const [order, setOrder] = useState("asc")
    const [orderBy, setOrderBy] = useState("id")

    // get document data in api
    useEffect(() => {
        const getDocument = () => {
            setisLoading(true);
            setPermissionToggle(true);
            setServerError(false);
            customAxios().get('/document/').then((res) => {
                setDocumentData(res.data.data);
                setpermission(res.data.permissions);
                setisLoading(false);
            }).catch((error) => {
                setisLoading(false)
                if (!error.response) {
                    setServerError(true)
                    toast.error(error.message)
                } else {
                    if (error.response.status === 500) {
                        setServerError(true)
                    }
                    if (error.response.data.message) {
                        toast.error(error.response.data.message)
                    }
                }
            }).finally(() => setPermissionToggle(false));
        }
        getDocument();
        // eslint-disable-next-line
    }, [toggle]);

    // delete function
    const handleDelete = (id) => {
        Swal.fire({
            title: 'Delete Document',
            text: "Are you sure you want to delete?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#1bcfb4',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            width: '450px',
        }).then(async (result) => {
            if (result.isConfirmed) {
                setisLoading(true)
                const res = await customAxios().delete(`/document/${id}`);
                if (res.data.success) {
                    setToggle(!toggle)
                    toast.success(res.data.message)
                }
            }
        }).catch((error) => {
            setisLoading(false)
            if (!error.response) {
                toast.error(error.message);
            } else if (error.response.data.message) {
                toast.error(error.response.data.message);
            }
        })
    }

    // memoize filtered items
    const documentDataFilter = useMemo(() => {
        return documentData.filter((item) => {
            return item.name.toLowerCase().includes(searchItem.toLowerCase()) ||
                item.description.toLowerCase().includes(searchItem.toLowerCase())
        })
    }, [documentData, searchItem]);

    // sort function
    const handleRequestSort = (name) => {
        const isAsc = (orderBy === name && order === "asc");

        setOrderBy(name)
        setOrder(isAsc ? "desc" : "asc")
    }

    const descedingComparator = (a, b, orderBy) => {
        if (b[orderBy] < a[orderBy]) {
            return -1
        }
        if (b[orderBy] > a[orderBy]) {
            return 1
        }
        return 0


    }

    const getComparator = (order, orderBy) => {
        return order === "desc" ? (a, b) => descedingComparator(a, b, orderBy) : (a, b) => -descedingComparator(a, b, orderBy)
    }

    const sortRowInformation = (array, comparator) => {
        const rowArray = array.map((elem, ind) => [elem, ind])

        rowArray.sort((a, b) => {
            const order = comparator(a[0], b[0])
            if (order !== 0) return order
            return a[1] - b[1]
        })
        return rowArray.map((el) => el[0])
    }

    const downloadFile = async (file, name) => {
        const ext = file.split(".").pop();
        setisLoading(true)
        axios.get(`${process.env.REACT_APP_API_KEY}/document/download?file=${file}`, {
            responseType: 'blob',
        }).then((res) => {
            fileDownload(res.data, name.concat(".", ext));
            toast.success("Download successfully.")
            setisLoading(false)
        }).catch((error) => {
                setisLoading(false);
                if (!error.response) {
                    toast.error(error.message);
                } else if (error.response.data.message) {
                    toast.error(error.response.data.message);
                } else {
                    toast.error(error.response.statusText);
                }
            })
    }


    if (isLoading) {
        return <Spinner />;
    } else if (serverError) {
        return <Error500 />;
    } else if ((!permission || permission.permissions.list !== 1) && !permissionToggle) {
        return <Error403 />;
    }

    return (
        <>
            <motion.div className="box" initial={{ opacity: 0, transform: 'translateY(-20px)' }} animate={{ opacity: 1, transform: 'translateY(0px)' }} transition={{ duration: 0.5 }}>
                <div className="container-fluid py-4">
                    <div className="background-wrapper bg-white pb-4">
                        <div className=''>
                            <div className='row justify-content-end align-items-center row-std m-0'>
                                <div className="col-12 col-sm-5 d-flex justify-content-between align-items-center">
                                    <div>
                                        <ul id="breadcrumb" className="mb-0">
                                            <li><NavLink to="/" className="ihome">Dashboard</NavLink></li>
                                            <li><NavLink to="/documents" className="ibeaker"><i className="fa-solid fa-play"></i> &nbsp; Documents</NavLink></li>
                                        </ul>
                                    </div>
                                </div>
                                <div className="col-12 col-sm-7 d-flex justify-content-end" id="two">
                                    <div className="search-full">
                                        <input type="search" className="input-search-full" autoComplete='off' value={searchItem} name="txt" placeholder="Search" onChange={(event) => setsearchItem(event.target.value)} />
                                        <i className="fas fa-search"></i>
                                    </div>
                                    <div className="search-box mr-3">
                                        <form name="search-inner">
                                            <input type="search" className="input-search" autoComplete='off' value={searchItem} name="txt" onChange={(event) => setsearchItem(event.target.value)} />
                                        </form>
                                        <i className="fas fa-search"></i>
                                    </div>
                                    <DocumentModalComponent setToggle={setToggle} toggle={toggle} permission={permission} />
                                </div>
                            </div>
                        </div>
                        {/* {table} */}
                        <div className="mx-4">
                            <TableContainer >
                                <Table className="common-table-section">
                                    <TableHead className="common-header">
                                        <TableRow>
                                            <TableCell>File</TableCell>
                                            <TableCell>
                                                <TableSortLabel active={orderBy === "name"} direction={orderBy === "name" ? order : "asc"} onClick={() => handleRequestSort("name")}>
                                                    File Name
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel active={orderBy === "description"} direction={orderBy === "description" ? order : "asc"} onClick={() => handleRequestSort("description")}>
                                                    Description
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                Action
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {documentDataFilter.length !== 0 ? sortRowInformation(documentDataFilter, getComparator(order, orderBy)).slice(rowsPerPage * page, rowsPerPage * page + rowsPerPage).map((val, ind) => {
                                            return (
                                                <TableRow key={ind}>
                                                    <TableCell>
                                                        <NavLink to={`${process.env.REACT_APP_IMAGE_API}/uploads/${val.image}`} target='_blank' >
                                                            <img
                                                                className='mt-1 profile-action-icon'
                                                                style={{ width: '70px', height: '70px' }}
                                                                src={(val.image.split(".").pop() !== 'doc' && val.image.split(".").pop() !== "pdf") ? `${process.env.REACT_APP_IMAGE_API}/uploads/${val.image}` : val.image.split(".").pop() === 'doc' ? '/Images/doc.png' : '/Images/pdf.png'}
                                                                alt="file"
                                                            />
                                                        </NavLink>
                                                    </TableCell>
                                                    <TableCell>{val.name}</TableCell>
                                                    <TableCell>{val.description}</TableCell>
                                                    <TableCell>
                                                        <div className='action'>
                                                            <i className="fa-solid fa-download" onClick={() => downloadFile(val.image, val.name)}></i>
                                                            {permission && permission.permissions.update === 1 && <DocumentModalComponent data={val} setToggle={setToggle} toggle={toggle} />}
                                                            {permission && permission.permissions.delete === 1 && <i className="fa-solid fa-trash-can" onClick={() => handleDelete(val._id)}></i>}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        }) :
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    No Records Found
                                                </TableCell>
                                            </TableRow>
                                        }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
                                component="div"
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPage={rowsPerPage}
                                count={documentDataFilter.length}
                                page={page}>
                            </TablePagination>
                        </div>
                    </div>
                </div>
            </motion.div>
        </>
    )
}

export default DocumentComponent
